import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, Trash2, Pencil, Check, X } from "lucide-react";

const CHIP_COLOR = {
    team: "#ff8a3d", trainer: "#4aa3f0", trainer_listing: "#4aa3f0",
    event: "#39d353", wall: "#c8a2ff",
};

function toLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

/** Lists the current user's scheduled posts (wall + boards) with edit/cancel. */
export default function ScheduledManager({ onChange }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // id
    const [draftBody, setDraftBody] = useState("");
    const [draftTime, setDraftTime] = useState("");

    const load = () => {
        setLoading(true);
        api.get("/social/scheduled")
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const startEdit = (it) => {
        setEditing(it.id);
        setDraftBody(it.body || "");
        setDraftTime(toLocalInput(it.scheduled_for));
    };

    const save = async (it) => {
        const payload = { body: draftBody };
        if (draftTime) {
            const d = new Date(draftTime);
            if (isNaN(d.getTime()) || d.getTime() <= Date.now()) return toast.error("Pick a future time");
            payload.scheduled_for = d.toISOString();
        }
        try {
            await api.patch(`/social/scheduled/${it.post_type}/${it.id}`, payload);
            toast.success("Scheduled post updated");
            setEditing(null);
            load();
            onChange?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update");
        }
    };

    const cancel = async (it) => {
        try {
            await api.delete(`/social/scheduled/${it.post_type}/${it.id}`);
            toast.success("Scheduled post canceled");
            load();
            onChange?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not cancel");
        }
    };

    if (loading || items.length === 0) return null;

    return (
        <div className="mb-4 border border-[#22c55e]/30 bg-[#101a12] p-3" data-testid="scheduled-manager">
            <div className="flex items-center gap-2 mb-2 text-[#22c55e]">
                <CalendarClock className="h-4 w-4" />
                <div className="text-xs uppercase tracking-[0.2em] font-semibold">Scheduled ({items.length})</div>
            </div>
            <div className="space-y-2">
                {items.map((it) => {
                    const color = CHIP_COLOR[it.context?.type] || "#c8a2ff";
                    const isEditing = editing === it.id;
                    return (
                        <div key={`${it.post_type}-${it.id}`} data-testid={`scheduled-item-${it.id}`} className="border border-[#2a2a2a] bg-[#0f0f0f] p-2.5">
                            <div className="flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-[0.12em]">
                                <span className="px-2 py-0.5 rounded-full" style={{ color, border: `1px solid ${color}55`, background: `${color}18` }}>{it.context?.name}</span>
                                <span className="text-[#22c55e]">{new Date(it.scheduled_for).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                            </div>
                            {isEditing ? (
                                <div className="mt-2 space-y-2">
                                    <textarea data-testid={`scheduled-edit-body-${it.id}`} value={draftBody} onChange={(e) => setDraftBody(e.target.value)} rows={2}
                                        className="w-full resize-none bg-[#141414] border border-[#2a2a2a] px-2 py-1.5 text-sm text-white outline-none focus:border-[#22c55e]" />
                                    <input type="datetime-local" data-testid={`scheduled-edit-time-${it.id}`} value={draftTime} min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                                        onChange={(e) => setDraftTime(e.target.value)} className="bg-[#141414] border border-[#2a2a2a] px-2 py-1 text-sm text-white outline-none" />
                                    <div className="flex gap-2">
                                        <button type="button" data-testid={`scheduled-save-${it.id}`} onClick={() => save(it)} className="inline-flex items-center gap-1 bg-[#22c55e] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 hover:bg-[#16a34a]"><Check className="h-3 w-3" /> Save</button>
                                        <button type="button" data-testid={`scheduled-canceledit-${it.id}`} onClick={() => setEditing(null)} className="inline-flex items-center gap-1 border border-[#2a2a2a] text-[#a0a0a0] text-[10px] uppercase tracking-wider px-3 py-1.5 hover:text-white"><X className="h-3 w-3" /> Cancel</button>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-1 flex items-start justify-between gap-2">
                                    <div className="text-sm text-white whitespace-pre-line break-words min-w-0">{it.subject && <span className="block font-semibold">{it.subject}</span>}{it.body || <span className="text-[#666] italic">No text</span>}</div>
                                    <div className="flex gap-1.5 shrink-0">
                                        <button type="button" data-testid={`scheduled-edit-${it.id}`} onClick={() => startEdit(it)} className="text-[#a0a0a0] hover:text-[#22c55e]" title="Edit"><Pencil className="h-4 w-4" /></button>
                                        <button type="button" data-testid={`scheduled-cancel-${it.id}`} onClick={() => cancel(it)} className="text-[#a0a0a0] hover:text-red-500" title="Cancel post"><Trash2 className="h-4 w-4" /></button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
